import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import InputField from 'src/shared/components/common/form_input/InputField'
import { InternationalPhoneInput } from 'src/shared/components/common/form_input/InternationalPhoneInput'
import LoadingSpinnerOverlayWrapper from 'src/shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal/Modal'
import { t } from 'src/translations/src'
import { GuestContact } from 'src/apollo/onlineOrdering'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import VerifyEmailModal from 'public/components/default_template/online_ordering/account/verifyEmailModal/VerifyEmailModal'
import { useEditAccountForm } from './hooks/useEditAccountForm'
import { useGuestAccountUpdate } from './hooks/useGuestAccountUpdate'
import { useEmailVerification } from './hooks/useEmailVerification'
import { showEmailUpdateSuccess } from './utils/accountNotifications'
import Image from 'src/shared/components/common/Image'

export const EditAccountModal = ({
  isOpen,
  onClose,
  setIsLoadingEmailVerification,
  contact,
  refetchGuestData
}: {
  isOpen: boolean
  onClose: () => void
  setIsLoadingEmailVerification: (isLoading: boolean) => void
  contact: GuestContact
  refetchGuestData: () => Promise<any>
}) => {
  const {
    updatedEmail,
    sendVerificationCode,
    clearUpdatedEmail,
    initiateEmailUpdate
  } = useEmailVerification()

  const onInitiateEmailUpdate = useCallback(
    async (email: string) => {
      setIsLoadingEmailVerification(true)
      return await initiateEmailUpdate(email)
    },
    [initiateEmailUpdate, setIsLoadingEmailVerification]
  )

  const handleEmailVerificationComplete = useCallback(
    async (didUpdateEmail: boolean) => {
      clearUpdatedEmail()
      if (didUpdateEmail) {
        showEmailUpdateSuccess()
      }
      setIsLoadingEmailVerification(false)
      onClose()
    },
    [clearUpdatedEmail, onClose, setIsLoadingEmailVerification]
  )

  useEffect(() => {
    if (updatedEmail) {
      setIsLoadingEmailVerification(false)
    }
  }, [updatedEmail, setIsLoadingEmailVerification])

  return (
    <>
      <VerifyEmailModal
        email={updatedEmail}
        isOpen={!!updatedEmail}
        onCloseModal={handleEmailVerificationComplete}
        resendCode={sendVerificationCode}
      />
      <Modal isOpen={isOpen} onClose={onClose} testId='edit-account-modal'>
        <ModalOverlay />
        <ModalContent contentClassName='fullScreenMobile accountPageModal modalContentV2 editAccountModal'>
          <div className='modalHeader'>
            <h2 className='headerText'>
              {t('edit-account-modal.edit-profile')}
            </h2>
          </div>
          <div className='modalBody' tabIndex={0}>
            <EditAccountForm
              onClose={onClose}
              initiateEmailUpdate={onInitiateEmailUpdate}
              contact={contact}
              refetchGuestData={refetchGuestData}
            />
          </div>
        </ModalContent>
      </Modal>
    </>
  )
}

const EditAccountForm = ({
  onClose,
  initiateEmailUpdate,
  contact,
  refetchGuestData
}: {
  onClose: () => void
  initiateEmailUpdate: (email: string) => Promise<boolean>
  contact: GuestContact
  refetchGuestData: () => Promise<any>
}) => {
  const [loading, setLoading] = useState(false)
  const { customer } = useCustomer()

  const formMethods = useEditAccountForm(contact)
  const { updateBasicInformation } = useGuestAccountUpdate(contact)

  // Form submission
  const onSubmit = useCallback(
    async (formData: any) => {
      setLoading(true)

      // Update basic info
      await updateBasicInformation(formData)

      // Close modal before updating email to avoid double modals
      onClose()

      // Update email if changed
      const emailChanged = contact?.email && formData.email !== contact.email
      if (emailChanged) {
        await initiateEmailUpdate(formData.email)
      }

      setLoading(false)
      await refetchGuestData()
    },
    [
      contact?.email,
      initiateEmailUpdate,
      updateBasicInformation,
      refetchGuestData,
      onClose
    ]
  )

  const emailValue = formMethods.getValues('email')

  const isEmailVerified =
    contact?.contactEmails?.[0]?.isVerified && emailValue === contact?.email

  const isDisabled = useMemo(
    () =>
      !(formMethods.formState.isValid && formMethods.formState.isDirty) ||
      formMethods.formState.isSubmitting ||
      !customer,
    [formMethods.formState, customer]
  )

  return (
    <>
      <FormProvider {...formMethods}>
        <div className='accountDetailsWrapper'>
          <div className='headerWrapper'>
            <h3 className='headerText'>
              {t('edit-account-modal.account-details')}
            </h3>
            <p className='loyaltyNotice'>
              {t('profile-information.loyalty-update-notice')}
            </p>
          </div>

          <form
            id='editAccountForm'
            onSubmit={formMethods.handleSubmit(onSubmit)}
          >
            <div className='nameInputWrapper'>
              <InputField
                disabled={!customer}
                id='firstName'
                type='text'
                label={t('profile-information.first-name')}
                required
              />
              <InputField
                disabled={!customer}
                id='lastName'
                type='text'
                label={t('profile-information.last-name')}
                required
              />
            </div>
            <div className='emailInputWrapper'>
              <InputField
                disabled={!customer}
                id='email'
                type='email'
                label={t('profile-information.email')}
                required
                classNames='emailInputToVerify'
              />
              {isEmailVerified && (
                <Image
                  alt={t('profile-information.email-verified')}
                  src='icons/check.svg'
                  className='verified'
                  data-testid='verified-icon'
                />
              )}
            </div>
            <InternationalPhoneInput
              disabled={!customer}
              id='phone'
              label={t('profile-information.phone')}
              readOnly={true}
              value={contact?.phone || ''}
            />
            {loading && <LoadingSpinnerOverlayWrapper />}
          </form>
        </div>
      </FormProvider>
      <div className='footer'>
        <button type='button' className='cancelButton' onClick={onClose}>
          {t('edit-account-modal.cancel')}
        </button>
        <button
          type='submit'
          className='primary'
          form='editAccountForm'
          disabled={isDisabled}
        >
          {t('edit-account-modal.save-changes')}
        </button>
      </div>
    </>
  )
}
